import React from "react";
import { Link } from "react-router-dom";
import {
  PawPrint,
  Sparkles,
  MessagesSquare,
  CalendarDays,
  Leaf,
  ShieldCheck,
  ArrowRight,
} from "lucide-react";
import PublicLayout from "../components/PublicLayout";

const APP_SCREENSHOT_HERO =
  "https://customer-assets.emergentagent.com/job_7d48fd7d-d1ca-4702-8ef0-f7e942a6f632/artifacts/5mi00iqo_bark_buddies_ipad_1_2048x2732.webp";
const APP_SCREENSHOT_2 =
  "https://customer-assets.emergentagent.com/job_7d48fd7d-d1ca-4702-8ef0-f7e942a6f632/artifacts/t27xspcx_bark_buddies_ipad_2_2048x2732.webp";
const APP_SCREENSHOT_3 =
  "https://customer-assets.emergentagent.com/job_7d48fd7d-d1ca-4702-8ef0-f7e942a6f632/artifacts/31vep3ou_bark_buddies_ipad_3_2048x2732.webp";
const LIFESTYLE =
  "https://images.unsplash.com/photo-1557495235-340eb888a9fb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHwzfHxoYXBweSUyMGRvZyUyMG93bmVyfGVufDB8fHx8MTc4MzU0NTk1MXww&ixlib=rb-4.1.0&q=85";

const features = [
  {
    icon: PawPrint,
    title: "Dog Profiles",
    text: "Create rich profiles for every pup — breed, size, energy, temperament, favorite activities, and photos.",
    testId: "feature-dog-profiles",
  },
  {
    icon: Sparkles,
    title: "Smart Matching",
    text: "Find compatible dogs nearby based on distance, size, energy, temperament, and owner preferences.",
    testId: "feature-smart-matching",
  },
  {
    icon: MessagesSquare,
    title: "Safe Messaging",
    text: "Chat only after a buddy request is accepted. Built-in safety reminders and report / block tools.",
    testId: "feature-safe-messaging",
  },
  {
    icon: CalendarDays,
    title: "Meetups",
    text: "Schedule dog playdates with date, time, location, notes, and attending dogs — all in one place.",
    testId: "feature-meetups",
  },
  {
    icon: Leaf,
    title: "Dog Parks",
    text: "Discover local dog parks, search by features, and find places that fit your dog's needs. Open in Maps.",
    testId: "feature-dog-parks",
  },
  {
    icon: ShieldCheck,
    title: "Safety First",
    text: "Bark Buddies encourages first meetups in public places, leashed introductions, and responsible communication.",
    testId: "feature-safety-first",
  },
];

function FeatureCard({ Icon, title, text, testId, delay }) {
  return (
    <div
      className="bg-white rounded-[24px] p-6 md:p-7 shadow-bark-soft hover:shadow-bark-lift hover:-translate-y-1 transition-[transform,box-shadow] duration-300 animate-fade-up"
      style={{ animationDelay: `${delay}ms` }}
      data-testid={testId}
    >
      <div className="w-11 h-11 rounded-2xl bg-bark-teal-soft grid place-items-center mb-4">
        <Icon size={22} className="text-bark-teal" strokeWidth={2.25} />
      </div>
      <h3 className="text-[18px] font-extrabold text-bark-ink mb-1.5 tracking-tight">
        {title}
      </h3>
      <p className="text-[14px] leading-relaxed text-bark-ink2">{text}</p>
    </div>
  );
}

export default function HomePage() {
  const scrollToFeatures = () => {
    document.getElementById("features")?.scrollIntoView({ behavior: "smooth" });
  };

  React.useEffect(() => {
    document.title = "Bark Buddies | Dog Playdate App";
  }, []);

  return (
    <PublicLayout>
      {/* HERO */}
      <section className="relative overflow-hidden" data-testid="home-hero">
        {/* teal blob */}
        <div
          aria-hidden
          className="absolute -top-24 -right-24 w-[520px] h-[520px] rounded-full bg-bark-teal/15 blur-3xl"
        />
        <div
          aria-hidden
          className="absolute top-40 -left-24 w-[380px] h-[380px] rounded-full bg-bark-teal/10 blur-3xl"
        />

        <div className="mx-auto max-w-6xl px-5 md:px-8 pt-14 md:pt-20 pb-16 md:pb-24 grid md:grid-cols-2 gap-12 md:gap-16 items-center relative">
          <div className="animate-fade-up">
            <div className="inline-flex items-center gap-2 bg-white/80 backdrop-blur px-4 py-1.5 rounded-full text-[12px] font-bold uppercase tracking-widest text-bark-teal shadow-bark-soft">
              <PawPrint size={14} /> Now in Beta
            </div>
            <h1 className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight text-bark-ink leading-[1.05]">
              Find your dog's <br className="hidden sm:block" />
              next <span className="text-bark-teal">best friend.</span>
            </h1>
            <p className="mt-5 text-[16px] md:text-[17px] leading-relaxed text-bark-ink2 max-w-xl">
              Bark Buddies helps dog owners create dog profiles, find compatible
              nearby dogs, send buddy requests, message safely, schedule
              meetups, and discover local dog parks.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Link
                to="/beta"
                className="inline-flex items-center gap-2 rounded-full bg-bark-teal px-7 py-3.5 text-white font-extrabold text-[15px] shadow-bark-soft hover:bg-bark-teal-hover hover:-translate-y-0.5 transition-transform duration-200"
                data-testid="hero-cta-join-beta"
              >
                Join the Android Beta
                <ArrowRight size={18} />
              </Link>
              <button
                onClick={scrollToFeatures}
                className="inline-flex items-center gap-2 rounded-full bg-white px-7 py-3.5 text-bark-ink font-extrabold text-[15px] shadow-bark-soft hover:-translate-y-0.5 transition-transform duration-200"
                data-testid="hero-cta-learn-more"
              >
                Learn More
              </button>
            </div>
            <div className="mt-8 flex items-center gap-5 text-[13px] text-bark-muted">
              <div className="flex items-center gap-1.5">
                <ShieldCheck size={16} className="text-bark-teal" />
                Safe by design
              </div>
              <div className="flex items-center gap-1.5">
                <PawPrint size={16} className="text-bark-teal" />
                For every breed
              </div>
            </div>
          </div>

          {/* App mockup collage */}
          <div className="relative animate-fade-up" style={{ animationDelay: "150ms" }}>
            <div className="relative mx-auto w-full max-w-[420px]">
              <div className="absolute -inset-8 bg-bark-teal/10 rounded-[48px] blur-2xl" aria-hidden />
              <div className="relative bg-white rounded-[36px] p-3 shadow-bark-lift">
                <img
                  src={`${process.env.PUBLIC_URL}/assets/hero-app-home.webp`}
                  alt="Bark Buddies app home screen"
                  className="w-full rounded-[28px] object-cover"
                />
              </div>
              <div className="hidden sm:block absolute -bottom-10 -left-10 w-[180px] rounded-[24px] shadow-bark-lift overflow-hidden bg-white p-2 rotate-[-6deg] animate-float-slow">
                <img
                  src={`${process.env.PUBLIC_URL}/assets/home-screen.webp`}
                  alt="Bark Buddies matches screen"
                  className="w-full rounded-[18px] object-cover"
                />
              </div>
              <div
                className="hidden sm:block absolute -top-8 -right-8 w-[160px] rounded-[24px] shadow-bark-lift overflow-hidden bg-white p-2 rotate-[7deg] animate-float-slow"
                style={{ animationDelay: "1.5s" }}
              >
                <img
                  src={`${process.env.PUBLIC_URL}/assets/matches-screen.webp`}
                  alt="Bark Buddies features screen"
                  className="w-full rounded-[18px] object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section
        id="features"
        className="relative py-16 md:py-24"
        data-testid="home-features"
      >
        <div className="mx-auto max-w-6xl px-5 md:px-8">
          <div className="max-w-2xl mb-12">
            <div className="text-[12px] font-bold uppercase tracking-widest text-bark-teal">
              What's inside
            </div>
            <h2 className="mt-2 text-3xl md:text-4xl font-black tracking-tight text-bark-ink">
              Everything for happier, safer playdates
            </h2>
            <p className="mt-3 text-[16px] text-bark-ink2 leading-relaxed">
              Bark Buddies is built for real dog owners — playful, shy,
              energetic, calm, small, large, young, senior, or with special
              needs.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {features.map((f, i) => (
              <FeatureCard
                key={f.title}
                Icon={f.icon}
                title={f.title}
                text={f.text}
                testId={f.testId}
                delay={i * 80}
              />
            ))}
          </div>
        </div>
      </section>

      {/* CTA BAND */}
      <section className="relative py-16" data-testid="home-cta-band">
        <div className="mx-auto max-w-6xl px-5 md:px-8">
          <div className="relative overflow-hidden bg-white rounded-[28px] shadow-bark-soft p-8 md:p-12 grid md:grid-cols-[1.2fr_1fr] gap-8 items-center">
            <div>
              <h3 className="text-2xl md:text-3xl font-black tracking-tight text-bark-ink">
                Help us shape Bark Buddies before public launch.
              </h3>
              <p className="mt-3 text-[15px] text-bark-ink2 leading-relaxed max-w-lg">
                Beta testers get early access, help us prioritize features, and
                make the app safer & more useful for every dog owner.
              </p>
              <Link
                to="/beta"
                className="mt-6 inline-flex items-center gap-2 rounded-full bg-bark-teal px-7 py-3.5 text-white font-extrabold text-[15px] shadow-bark-soft hover:bg-bark-teal-hover hover:-translate-y-0.5 transition-transform duration-200"
                data-testid="cta-band-join-beta"
              >
                Become a Beta Tester
                <ArrowRight size={18} />
              </Link>
            </div>
            <div className="relative">
              <img
                src={`${process.env.PUBLIC_URL}/assets/features-screen.webp`}
                alt="Happy owner and dog"
                className="w-full h-56 md:h-64 object-cover rounded-[20px] shadow-bark-soft"
              />
            </div>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}
